import type { InterventoEvento } from 'api'

import { trpc } from '@/clients/trpc'
import { withoutUnsentMarker } from './helpers'
import { isMissingProcedure } from './pull-checkpoint'
import { reportSyncFailure } from './report-sync-failure'

type Model = 'interventi' | 'foto_interventi'

/** What the server didn't take from a push (sync.push names it; pushChanges can't). */
export interface Refused {
  type: 'evento' | 'intervento' | 'foto_intervento' | 'committente'
  id: string
}

export interface PushAnswer<R> {
  records: R[]
  refused: Refused[]
}

/**
 * Pushes rows with sync.push, which answers with the server's row for every row
 * the pusher may pull, so the phone replaces its copies with what the server
 * decided. Falls back to pushChanges, which answers only for the rows it wrote,
 * when the API has no sync.push yet (the UI deployed first, or the API rolled
 * back).
 */
export function pushToServer<R>(modelName: Model) {
  const push = pushWithFallback<R>(modelName)
  // Reported like pull failures (K42, K44)
  return (documents: R[], eventi?: InterventoEvento[]) =>
    push(documents, eventi).catch((error: unknown) => {
      reportSyncFailure(`trpc-${modelName}-push`, error)
      throw error
    })
}

function pushWithFallback<R>(modelName: Model) {
  return async (
    documents: R[],
    eventi?: InterventoEvento[]
  ): Promise<PushAnswer<R>> => {
    const input = {
      modelName,
      // The marker stays on the device: the API takes any field and logs the
      // documents whole (sync_requests)
      documents: (documents as Array<Record<string, unknown>>).map(
        withoutUnsentMarker
      ),
      ...(eventi && { eventi }),
    }
    try {
      const response = await trpc.sync.push.mutate(input)
      return {
        records: response.records as unknown as R[],
        refused: (response.refused ?? []) as Refused[],
      }
    } catch (error) {
      if (!isMissingProcedure(error)) throw error
    }

    const response = await trpc.sync.pushChanges.mutate(input)
    return { records: response.records as unknown as R[], refused: [] }
  }
}
